import { useEffect, useRef } from 'react';
import {
  Asset,
  FilterGroupingEnum,
  InterventionScopeNameEnum,
  useAssetWithCountersQuery,
} from 'graphql-common';
import { ErrorLike } from '@apollo/client';

type Args = {
  id: string;
  includeFullData: boolean;
  isDocumentationLocked?: boolean;
  isInterventionsLocked?: boolean;
  onError?: (error: ErrorLike) => void;
};

export type AssetCounters = {
  counters: number;
  nomenclature: number;
  documentations: number;
  interventions: number;
};

export type UseAssetQueryHookResult = {
  counters: AssetCounters;
  data: Asset | null | undefined;
  error: ErrorLike | undefined;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => Promise<unknown>;
};

export default function useAssetWithCountersQueryHook(
  args: Args,
): UseAssetQueryHookResult {
  const {
    id,
    includeFullData,
    isDocumentationLocked = false,
    isInterventionsLocked = false,
    onError,
  } = args;
  const query = useAssetWithCountersQuery({
    skip: !id,
    variables: {
      findBy: { id: id || '' },
      includeFullData,
      mainAssetId: id,
      nomenclaturesFilters: undefined,
      documentationsFilters: [
        {
          linkedAssetsId: {
            predicate: {
              eq: id,
            },
          },
        },
      ],
      interventionsFilters: [
        {
          assetId: {
            grouping: FilterGroupingEnum.And,
            predicate: {
              eq: id,
            },
            filterGroup: 'assetFilter',
          },
        },
      ],
      interventionsScopeName: InterventionScopeNameEnum.All,
      isDocumentationLocked,
      isInterventionsLocked,
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: queryData, refetch, loading, error, previousData } = query;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);
  const data = queryData || previousData;
  const assetData = data?.data as Asset;
  const firstLoading = loading && queryData === undefined;
  // Read the counters off `data`, not `queryData`: the tab bar that shows them
  // stays mounted while the query refetches for a new variable set (the
  // `includeFullData` flip on an About ↔ tab hop), and `queryData` is undefined
  // for the whole of that window. Falling back to `previousData` holds the last
  // known numbers instead of blinking every counter down to 0 and back.
  const counters = {
    nomenclature: data?.nomenclatures?.metadata?.totalCount || 0,
    counters: data?.counters?.metadata?.totalCount || 0,
    documentations: data?.documentations?.metadata?.totalCount || 0,
    interventions: data?.interventions?.metadata?.totalCount || 0,
  };

  return {
    counters,
    data: assetData,
    error,
    firstLoading,
    loading,
    refetch,
  };
}
